import { onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { requiresProctorMfa } from '@/lib/proctor-roles'
import {
  clearLastActivity,
  isIdleExpired,
  readLastActivity,
  writeLastActivity,
} from '@/lib/idle-session'

const ACTIVITY_EVENTS = [
  'pointerdown',
  'pointermove',
  'keydown',
  'wheel',
  'touchstart',
  'scroll',
] as const

// Persisting every event would hammer localStorage on pointermove/scroll;
// 15s keeps other tabs' sliding window close to the truth without the churn.
const PERSIST_THROTTLE_MS = 15_000
const CHECK_INTERVAL_MS = 30_000

/**
 * Enforces the 2-hour sliding idle timeout. Mounted once in App.vue.
 *
 * - User activity updates the stored last-activity timestamp (throttled).
 * - Every 30s — and immediately when a hidden tab becomes visible again —
 *   an authenticated session whose window lapsed is signed out and routed
 *   to the matching login page with ?error=session_expired.
 * - Sign-in seeds a fresh window; sign-out drops the record.
 */
export function useIdleSession(): void {
  const auth = useAuthStore()
  const router = useRouter()
  let lastPersisted = 0
  let checkTimer: ReturnType<typeof setInterval> | null = null
  let expiring = false

  function recordActivity(): void {
    // Only authenticated sessions have an idle window — interaction on the
    // login page must not count against the session that hasn't started yet.
    if (!auth.isAuthenticated) return
    const now = Date.now()
    if (now - lastPersisted >= PERSIST_THROTTLE_MS) {
      writeLastActivity(now)
      lastPersisted = now
    }
  }

  async function expire(): Promise<void> {
    if (!auth.isAuthenticated || expiring) return
    expiring = true
    try {
      const route = router.currentRoute.value
      const proctorTarget = requiresProctorMfa(auth.role)
        || route.meta.requiresProctorMfa === true
        || route.meta.allowsProctorEnrollment === true
      try {
        await auth.signOut()
      } finally {
        // signOut can reject on storage-blocked browsers; the local state is
        // already cleared, so the user must still land on the login page.
        await router.push({
          name: proctorTarget ? 'proctor-login' : 'login',
          query: { error: 'session_expired', next: route.fullPath },
        })
      }
    } finally {
      // Only guards against overlapping calls — a session that signs back
      // in within this page's lifetime must be able to expire again.
      expiring = false
    }
  }

  function checkExpired(): void {
    if (!auth.isAuthenticated) return
    const last = readLastActivity()
    if (last === null) {
      // An authenticated session with no record gets a fresh window rather
      // than none — storage cleared by hand, or another tab signed out
      // before Firebase's own sign-out broadcast arrived.
      writeLastActivity()
      lastPersisted = Date.now()
      return
    }
    if (isIdleExpired(last)) void expire()
  }

  function onVisibilityChange(): void {
    // Interval timers are throttled in background tabs, so the lapse is only
    // noticed reliably when the user comes back — check it on the way in.
    if (document.visibilityState === 'visible') checkExpired()
  }

  watch(
    () => [auth.ready, auth.isAuthenticated] as const,
    ([ready, authenticated]) => {
      if (authenticated) {
        checkExpired()
      } else if (ready) {
        // Only clear once Firebase has resolved. Before `ready`, a persisted
        // session may still restore — wiping the timestamp on every cold
        // start would reset the idle window on each page reload.
        clearLastActivity()
      }
    },
    { immediate: true },
  )

  onMounted(() => {
    for (const event of ACTIVITY_EVENTS) {
      // Capture: scroll does not bubble, so it must be seen on the way down.
      window.addEventListener(event, recordActivity, { capture: true, passive: true })
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    checkTimer = setInterval(checkExpired, CHECK_INTERVAL_MS)
    checkExpired()
  })

  onUnmounted(() => {
    for (const event of ACTIVITY_EVENTS) {
      window.removeEventListener(event, recordActivity, { capture: true })
    }
    document.removeEventListener('visibilitychange', onVisibilityChange)
    if (checkTimer) clearInterval(checkTimer)
  })
}
